import * as React from "react";
import { useEffect, useState } from "react";
import {
    Box, Typography, List, ListItem,
    TextField, Select, MenuItem, FormControl,
    FormControlLabel, Checkbox, Button, InputLabel, InputAdornment, Chip
} from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { getDateFormatByTimezone } from "../../utils";

import { IQuestion, IEventCaptureFormProps } from "../../types"; import TCamera from "../Common/TCamera";


export default function AdditionalForms({
    setIsFormValid = () => { },
    setSelectedFormFields = () => { },
    selectedFormFields = {},
    isformPreview,
    previewQuestions = [],
    formDetails
}: IEventCaptureFormProps) {
    const [questions, setQuestions] = React.useState<IQuestion[]>([]);
    const [errors, setErrors] = useState({});
    const [photoDataUris, setPhotoDataUris] = useState({});

    // Merge multiple forms into a single array
    useEffect(() => {
        if (isformPreview) {
            setQuestions(previewQuestions);
        } else if (formDetails) {
            const combinedQuestions = formDetails.flatMap((form) =>
                form.formFields?.map((field) => ({ ...field, formId: form.id }))
            );
            setQuestions(combinedQuestions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDetails, isformPreview]);

    // Handle input change for all field types
    const handleInputChange = (formId, fieldId, value, fieldType) => {
        if (fieldType === "datetime") {
            value = dayjs(value).format("DD-MM-YYYY hh-mm-a");
        }
        setSelectedFormFields((prevData) => ({
            ...prevData,
            [formId]: {
                ...prevData[formId],
                [`${fieldType}_${fieldId}`]: { value, fieldType }
            }
        }));
        // Validate Number and Short Answer fields
        if (fieldType === "number") {
            const question = questions.find(q => q.id === fieldId && q.formId === formId);
            if (question) {
                const { min, max } = question;
                if ((min !== null && value < min) || (max !== null && value > max)) {
                    setErrors((prevErrors) => ({
                        ...prevErrors,
                        [`${formId}_${fieldId}`]: `Please enter a number between ${min} and ${max}`
                    }));
                } else {
                    setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        delete newErrors[`${formId}_${fieldId}`];
                        return newErrors;
                    });
                }
            }
        } else if (fieldType === "shortanswer") {
            if (value?.length > 200) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [`${formId}_${fieldId}`]: "Please enter no more than 200 characters"
                }));
            } else {
                setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    delete newErrors[`${formId}_${fieldId}`];
                    return newErrors;
                });
            }
        }
    };

    // Validate form on every change
    useEffect(() => {
        const validateForm = () => {
            const isFormValid = questions?.every((question) => {
                if (question.required) {
                    const fieldValue = selectedFormFields[question.formId]?.[`${question.type}_${question.id}`]?.value;

                    // Handle different field types
                    switch (question.type) {
                        case 'fileupload':
                            // Check if files array exists and has valid files
                            return Array.isArray(fieldValue) &&
                                fieldValue?.length > 0 &&
                                fieldValue.every(file => file !== undefined);

                        case 'datetime':
                            // Check if date is valid and not "Invalid Date"
                            return fieldValue &&
                                dayjs(fieldValue, "DD-MM-YYYY hh-mm-a").isValid();

                        default:
                            // Handle other field types (dropdown, multiplechoice, etc.)
                            return fieldValue && (!Array.isArray(fieldValue) || fieldValue?.length > 0);
                    }
                }
                return true;
            });
            setIsFormValid(isFormValid && Object.keys(errors)?.length === 0);
        };
        validateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFormFields, questions, errors]);

    // Handle multiple choice field type
    const handleMultipleChoiceChange = (event, question) => {
        const { value, checked } = event.target;
        setSelectedFormFields((prevData) => {
            const prevValues = prevData[question.formId]?.[`${question.type}_${question.id}`]?.value || [];
            const newValues = checked ? [...prevValues, value] : prevValues.filter((v) => v !== value);
            return {
                ...prevData,
                [question.formId]: {
                    ...prevData[question.formId],
                    [`${question.type}_${question.id}`]: { value: newValues, fieldType: question.type }
                }
            };
        });
    };


    const handleFileUpload = (event, question) => {
        const { files } = event.target;
        const allowedTypes = question.allowedFileTypes.includes("pdf") ?
            ["application/pdf"] :
            question.allowedFileTypes.includes("document") ?
                ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"] :
                ["image/png", "image/jpg", "image/jpeg"];
        const maxFiles = question.maxFiles || 2;
        const maxSize = 2 * 1024 * 1024;

        const existingFiles = selectedFormFields[question.formId]?.[`${question.type}_${question.id}`]?.value || [];
        const newFiles = Array.from(files);

        if (existingFiles?.length + newFiles?.length > maxFiles) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [`${question.formId}_${question.id}`]: `You can upload a maximum of ${maxFiles} files`
            }));
            event.target.value = null;
            return;
        }

        for (const file of newFiles) {
            if (!allowedTypes.includes((file as File).type)) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [`${question.formId}_${question.id}`]: `Only ${allowedTypes.join(", ")} formats are allowed`
                }));
                event.target.value = null;
                return;
            }
            if ((file as File).size > maxSize) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [`${question.formId}_${question.id}`]: "File size should not exceed 2 MB"
                }));
                event.target.value = null;
                return;
            }
        }

        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[`${question.formId}_${question.id}`];
            return newErrors;
        });

        setSelectedFormFields((prevData) => ({
            ...prevData,
            [question.formId]: {
                ...prevData[question.formId],
                [`${question.type}_${question.id}`]: {
                    value: [...existingFiles, ...newFiles],
                    fieldType: question.type
                }
            }
        }));
        event.target.value = null;
    };

    const handleFileDelete = (question, fileToDelete) => {
        setSelectedFormFields((prevData) => {
            const updatedFiles = prevData[question.formId]?.[`${question.type}_${question.id}`]?.value.filter(
                (file) => file !== fileToDelete
            );
            return {
                ...prevData,
                [question.formId]: {
                    ...prevData[question.formId],
                    [`${question.type}_${question.id}`]: { value: updatedFiles, fieldType: question.type }
                }
            };
        });

        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[`${question.formId}_${question.id}`];
            return newErrors;
        });
    };

    const handlePhotoData = (formId, fieldId, file) => {
        setSelectedFormFields((prevData) => ({
            ...prevData,
            [formId]: {
                ...prevData[formId],
                [`fileupload_${fieldId}`]: { value: [file], fieldType: "fileupload" }
            }
        }));
    };

    const handleSetPhotoDataUri = (formId, fieldId, dataUri) => {
        setPhotoDataUris((prevUris) => ({
            ...prevUris,
            [`${formId}_${fieldId}`]: dataUri
        }));
    };

    // Render input fields based on field type
    const renderQuestionInput = (question: IQuestion) => {
        const handleChange = (event) => {
            handleInputChange(question.formId, question.id, event.target.value, question.type);
        };

        const getValue = (formId, fieldId, fieldType) =>
            selectedFormFields[formId]?.[`${fieldType}_${fieldId}`]?.value || "";

        switch (question.type) {
            case "shortanswer":
                return (
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            multiline
                            maxRows={3}
                            label={question.text}
                            value={getValue(question.formId, question.id, question.type)}
                            onChange={handleChange}
                            sx={{ mt: 1 }}
                            required={question.required}
                            InputProps={{
                                sx: {
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgb(25, 118, 210)",
                                    },
                                },
                            }}
                            InputLabelProps={{
                                sx: {
                                    "&.Mui-focused": {
                                        color: "rgb(25, 118, 210)",
                                    },
                                },
                            }}
                        />
                        {errors[`${question.formId}_${question.id}`] && (
                            <Typography color="error" variant="body2">
                                {errors[`${question.formId}_${question.id}`]}
                            </Typography>
                        )}
                    </Box>
                );
            case "dropdown":
                return (
                    <Box sx={{ display: "flex", width: "100%" }}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel
                                id={`forms-select-${question.id}`}
                                sx={{
                                    "&.Mui-focused": {
                                        color: "rgb(25, 118, 210)",
                                    },
                                }}
                                required={question.required}
                            >{question.text}</InputLabel>
                            <Select
                                labelId={`forms-select-${question.id}`}
                                id={`forms-select-${question.id}`}
                                label={question.text}
                                value={getValue(question.formId, question.id, question.type)}
                                onChange={handleChange}
                                sx={{
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgb(25, 118, 210)",
                                    },
                                }}
                            >
                                {question?.options?.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                );
            case "multiplechoice":
                return (
                    <Box display="flex" flexDirection="row" justifyContent="flex-start" flexWrap="wrap">
                        {question?.options?.map((option) => (
                            <FormControlLabel
                                key={option}
                                control={
                                    <Checkbox
                                        value={option}
                                        checked={getValue(question.formId, question.id, question.type).includes(option)}
                                        onChange={(event) => handleMultipleChoiceChange(event, question)}
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'rgb(25, 118, 210)',
                                            },
                                        }}
                                    />
                                }
                                label={option}
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        color: 'rgba(0, 0, 0, 0.87)',
                                    }
                                }}
                            />
                        ))}
                    </Box>
                );
            case "time":
                return <TextField variant="outlined" type="time" required={question.required} onChange={handleChange} />;
            case "fileupload":
                if (question.allowedFileTypes?.includes("cam")) {
                    return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
                        <TCamera
                            onClose={(file) => handlePhotoData(question.formId, question.id, file)}
                            setPhotoDataUri={(dataUri) => handleSetPhotoDataUri(question.formId, question.id, dataUri)}
                            ismetaForm
                            disabled={false}
                            photoDataUri={photoDataUris[`${question.formId}_${question.id}`] || getValue(question.formId, question.id, question.type)}
                        />
                    </Box>;
                }
                if (question.allowedFileTypes?.includes("image")
                    || question.allowedFileTypes?.includes("pdf") || question.allowedFileTypes?.includes("document")) {
                    const files =
                        selectedFormFields[question.formId]?.[`${question.type}_${question.id}`]?.value || [];
                    return (
                        <Box sx={{
                            display: "flex", flexDirection: "column",
                            alignItems: "center", mx: "auto",
                            width: "100%"
                        }} >
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="file"
                                required={question.required}
                                inputProps={{
                                    multiple: true,
                                    accept: question.allowedFileTypes.includes("pdf") ? "application/pdf" :
                                        question.allowedFileTypes.includes("document") ?
                                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" :
                                            "image/png, image/jpg, image/jpeg",
                                    style: { display: "none" }
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                    sx: {
                                        "&.Mui-focused": {
                                            color: "rgb(25, 118, 210)",
                                        },
                                    },
                                }}
                                InputProps={{
                                    sx: {
                                        width: "100%",
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "rgb(25, 118, 210)",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "4px",
                                        }
                                    },
                                    endAdornment: (
                                        <Button
                                            variant="text"
                                            component="label"
                                            fullWidth
                                            sx={{
                                                py: 1.5,
                                                width: "100%",
                                                color: "rgb(25, 118, 210)",
                                                "&:hover": {
                                                    backgroundColor: "rgba(25, 118, 210, 0.04)"
                                                },
                                                textTransform: 'none',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Choose File
                                            <input
                                                type="file"
                                                hidden
                                                multiple
                                                accept={question.allowedFileTypes.includes("pdf") ? "application/pdf" :
                                                    question.allowedFileTypes.includes("document") ?
                                                        // eslint-disable-next-line max-len
                                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" :
                                                        "image/png, image/jpg, image/jpeg"}
                                                onChange={(event) => handleFileUpload(event, question)}
                                            />
                                        </Button>
                                    )
                                }}
                            />
                            {errors[`${question.formId}_${question.id}`] && (
                                <Typography color="error" variant="body2">
                                    {errors[`${question.formId}_${question.id}`]}
                                </Typography>
                            )}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                {Array.isArray(files) && files.map((file) => (
                                    <Chip
                                        key={file.name}
                                        label={file.name?.length > 10 ? `${file.name.substring(0, 10)}...` : file.name}
                                        onDelete={() => handleFileDelete(question, file)}
                                        sx={{
                                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                            color: 'rgb(25, 118, 210)',
                                            '& .MuiChip-deleteIcon': {
                                                color: 'rgb(25, 118, 210)',
                                                '&:hover': {
                                                    color: 'rgb(25, 118, 210)',
                                                }
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    );
                }
                return <TextField variant="outlined" type="file" onChange={handleChange} />;
            case "number":
                return (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <TextField
                            variant="outlined"
                            type="number"
                            label={question.text}
                            value={getValue(question.formId, question.id, question.type)}
                            onChange={handleChange}
                            required={question.required}
                            error={!!errors[`${question.formId}_${question.id}`]}
                            helperText={errors[`${question.formId}_${question.id}`]}
                            InputProps={{
                                sx: {
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgb(25, 118, 210)",
                                    },
                                },
                                endAdornment: <InputAdornment position="start">
                                    {question.uom && (
                                        <Typography variant="body2" sx={{ ml: 1 }}>
                                            {question.uom}
                                        </Typography>
                                    )}</InputAdornment>
                            }}
                            InputLabelProps={{
                                sx: {
                                    "&.Mui-focused": {
                                        color: "rgb(25, 118, 210)",
                                    },
                                },
                            }}
                        />
                    </Box>
                );
            case "datetime":
                return (
                    <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} >
                            <DemoContainer components={["DateTimePicker"]}>
                                <DateTimePicker
                                    format={getDateFormatByTimezone(true)}
                                    value={getValue(question.formId, question.id, question.type) ?
                                        dayjs(getValue(question.formId, question.id, question.type), "DD-MM-YYYY hh-mm-a") :
                                        null}
                                    onChange={(newValue) => handleInputChange(question.formId,
                                        question.id, newValue, question.type)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgb(25, 118, 210)',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: 'rgb(25, 118, 210)',
                                        },
                                    }}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                    </Box>
                )
            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "600px", margin: "0 auto" }} >
            <List sx={{ width: "100%" }}>
                {questions?.map((question) => (
                    <ListItem key={question.id} sx={{ mb: 1, width: "100%", px: 0 }}>
                        <Box sx={{ width: "100%" }}>
                            {(["multiplechoice", "datetime", "fileupload"].includes(question.type) &&
                                <Box sx={{ width: "100%", textAlign: "center", mb: 1, px: 0 }}>
                                    <Typography>{question.text}</Typography>
                                    {question.type.includes("fileupload") && (
                                        <Typography mb={0.5} fontSize={13} color="GrayText">
                                            (You can upload up to {question.maxFiles} files in{" "}
                                            {question?.allowedFileTypes?.join(", ") || "unknown"} format)
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {renderQuestionInput(question)}
                        </Box>
                    </ListItem>
                ))}
            </List>
        </Box>
    )
}
